// First-party, cookieless site analytics: one small beacon per page view or
// tracked action to the API's POST /pulse, shown on the admin dashboard.
// Nothing is stored on the visitor's device — the API counts visitors from
// a daily-rotating hash (see the API's src/services/siteAnalytics.js).
//
// Fire-and-forget: tracking must never break or slow down the page.

const baseURL =
  process.env.REACT_APP_API_BASE_URL || 'https://api.there2b.com/api';
const ENDPOINT = `${baseURL.replace(/\/$/, '')}/pulse`;

const enabled = () =>
  typeof window !== 'undefined' &&
  process.env.NODE_ENV !== 'test' &&
  process.env.REACT_APP_ANALYTICS !== 'off' &&
  // Playwright and other automation: keep e2e runs out of the numbers.
  !navigator.webdriver;

const send = (payload) => {
  if (!enabled()) return;
  try {
    // text/plain keeps it a "simple" cross-origin request: no CORS preflight.
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'text/plain' });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }
    fetch(ENDPOINT, {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'text/plain' },
      credentials: 'omit',
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Ignore: analytics is best-effort.
  }
};

// The first page of a visit is the first one this page load shows, unless we
// got here from our own site (a link opened in a new tab). Nothing is stored
// on the device, so no cookie or storage consent is involved.
let visitStarted = false;
const isFirstPageOfVisit = () => {
  if (visitStarted) return false;
  visitStarted = true;
  try {
    return (
      !document.referrer ||
      new URL(document.referrer).origin !== window.location.origin
    );
  } catch {
    return true;
  }
};

// Where the visit came from: campaign tags win, then our own links (the
// promoter's apply link and email invites add ?apply=1 / ?invite=1).
const sourceFrom = (params) => {
  if (params.get('utm_source')) return params.get('utm_source');
  if (params.get('apply') === '1') return 'apply_link';
  if (params.get('invite') === '1') return 'email_invite';
  return undefined;
};

// A page view is a change of path. This also absorbs the tracker remounting
// (StrictMode, a provider re-rendering the router) and filters rewriting the
// query string. A full reload starts a fresh module, so it still counts.
let lastPath = null;

/** Record a page view. `location` is react-router's location. */
export const trackPageview = (location, { role } = {}) => {
  if (location.pathname === lastPath) return;
  lastPath = location.pathname;
  const payload = { t: 'pageview', p: location.pathname, role };
  if (isFirstPageOfVisit()) {
    const params = new URLSearchParams(location.search);
    Object.assign(payload, {
      entry: true,
      r: document.referrer || undefined,
      s: sourceFrom(params),
      m: params.get('utm_medium') || undefined,
      c: params.get('utm_campaign') || undefined,
    });
  }
  send(payload);
};

/**
 * Record an action tied to an event:
 *   event_view | book_click | login_to_book_click | share_link_copy | share_download
 */
export const trackAction = (type, eventId) => {
  if (!eventId) return;
  send({ t: type, p: window.location.pathname, e: eventId });
};
